import { Fragment, useEffect, useState } from "react";
import { isValidGstin, lookupGstin, normalizeGstin } from "@/lib/gstinLookup";
import axios from "axios";
import { Plus, Pencil, Trash, MagnifyingGlass, CaretDown, CaretRight, CaretLeft, TrendUp, TrendDown } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { GroupPicker } from "@/components/GroupPicker";
import { useFieldCfg } from "@/lib/softwareConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDIAN_STATES = [
  { name: "Andhra Pradesh", code: "37" },
  { name: "Arunachal Pradesh", code: "12" },
  { name: "Assam", code: "18" },
  { name: "Bihar", code: "10" },
  { name: "Chhattisgarh", code: "22" },
  { name: "Goa", code: "30" },
  { name: "Gujarat", code: "24" },
  { name: "Haryana", code: "06" },
  { name: "Himachal Pradesh", code: "02" },
  { name: "Jharkhand", code: "20" },
  { name: "Karnataka", code: "29" },
  { name: "Kerala", code: "32" },
  { name: "Madhya Pradesh", code: "23" },
  { name: "Maharashtra", code: "27" },
  { name: "Manipur", code: "14" },
  { name: "Meghalaya", code: "17" },
  { name: "Mizoram", code: "15" },
  { name: "Nagaland", code: "13" },
  { name: "Odisha", code: "21" },
  { name: "Punjab", code: "03" },
  { name: "Rajasthan", code: "08" },
  { name: "Sikkim", code: "11" },
  { name: "Tamil Nadu", code: "33" },
  { name: "Telangana", code: "36" },
  { name: "Tripura", code: "16" },
  { name: "Uttar Pradesh", code: "09" },
  { name: "Uttarakhand", code: "05" },
  { name: "West Bengal", code: "19" },
  { name: "Delhi", code: "07" },
];

export default function Customers() {
  const custFcfg = useFieldCfg("customer");
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [groups, setGroups] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [view, setView] = useState(null); // null | "sales" | "purchase"
  const [suppliers, setSuppliers] = useState([]);
  const [supEdit, setSupEdit] = useState(null);
  const [supSaving, setSupSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    gstin: "",
    address: "",
    city: "",
    state: "Maharashtra",
    state_code: "27",
    pincode: "",
    group_id: "",
  });

  useEffect(() => {
    fetchCustomers();
    axios.get(`${API}/account-groups`).then((r) => setGroups(r.data.groups || [])).catch(() => {});
    axios.get(`${API}/suppliers`).then((r) => setSuppliers(r.data.suppliers || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const salesCustomers = customers.filter((c) => (c.party_type || "sales") === "sales");
    const filtered = salesCustomers.filter(
      (customer) =>
        customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (customer.phone || "").includes(searchQuery) ||
        (customer.gstin || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (customer.email && customer.email.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    setFilteredCustomers(filtered);
  }, [searchQuery, customers]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.group_id) {
      alert("Please select a ledger group in the 'Under' field");
      return;
    }
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, formData);
      } else {
        await axios.post(`${API}/customers`, formData);
      }
      fetchCustomers();
      resetForm();
    } catch (error) {
      console.error("Error saving customer:", error);
      alert(error?.response?.data?.detail || "Failed to save customer");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(
      `Delete "${name || "this party"}"?\n\nParty aur uska poora data (sales/purchase bills, notes, payments, challans, transport invoices, godown entries, stock ledger) har jagah se hat jayega aur RECYCLE BIN me chala jayega.\n\nRecycle Bin se RESTORE karne par poora data wapas aa jayega. Bin me se permanently delete karne par hamesha ke liye chala jayega.`
    )) return;
    try {
      const r = await axios.delete(`${API}/customers/${id}`);
      const rem = r.data?.removed || {};
      const parts = Object.entries(rem).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`);
      alert(`"${r.data?.party || name}" moved to Recycle Bin${parts.length ? ` — removed everywhere: ${parts.join(", ")}` : ""}. Restore anytime from Recycle Bin.`);
      fetchCustomers();
    } catch (error) {
      alert("Delete failed: " + (error.response?.data?.detail || error.message));
      console.error("Error deleting customer:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      gstin: "",
      address: "",
      city: "",
      state: "Maharashtra",
      state_code: "27",
      pincode: "",
      group_id: "",
    });
    setEditingCustomer(null);
    setIsDialogOpen(false);
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email || "",
      phone: customer.phone || "",
      gstin: customer.gstin || "",
      address: customer.address,
      city: customer.city,
      state: customer.state,
      state_code: customer.state_code,
      pincode: customer.pincode,
      group_id: customer.group_id || "",
    });
    setIsDialogOpen(true);
  };

  const handleStateChange = (stateName) => {
    const state = INDIAN_STATES.find((s) => s.name === stateName);
    if (state) {
      setFormData({ ...formData, state: state.name, state_code: state.code });
    }
  };

  const [gstinFetch, setGstinFetch] = useState({ state: "idle", message: "" });

  const handleGstinChange = async (raw) => {
    const g = normalizeGstin(raw);
    setFormData((f) => ({ ...f, gstin: g }));
    if (g.length < 15) { setGstinFetch({ state: "idle", message: "" }); return; }
    if (!isValidGstin(g)) { setGstinFetch({ state: "invalid", message: "Invalid GSTIN format" }); return; }
    setGstinFetch({ state: "loading", message: "Fetching details from GST records…" });
    try {
      const d = await lookupGstin(g);
      if (d.found) {
        setFormData((f) => ({
          ...f,
          name: d.name || f.name,
          address: d.address || f.address,
          city: d.city || f.city,
          state: d.state || f.state,
          state_code: d.state_code || f.state_code,
          pincode: d.pincode || f.pincode,
          phone: d.phone || f.phone,
          email: d.email || f.email,
        }));
        const src = d.source === "local" ? "your existing records" : "GST records";
        setGstinFetch({ state: "done", message: `✓ ${d.name}${d.status ? ` (${d.status})` : ""} — details auto-filled from ${src}` });
      } else {
        setFormData((f) => ({ ...f, state: d.state || f.state, state_code: d.state_code || f.state_code }));
        setGstinFetch({ state: "partial", message: d.message || "Only state auto-filled" });
      }
    } catch (err) {
      setGstinFetch({ state: "error", message: err?.response?.data?.detail || "Lookup failed" });
    }
  };

  const salesCustomers = customers.filter((c) => (c.party_type || "sales") === "sales");
  const openSupplierEdit = (s) => {
    setSupEdit({ id: s.id, name: s.name || "", gstin: s.gstin || "", phone: s.phone || "", address: s.address || "",
      state: s.state || "", state_code: s.state_code || "", group_id: "", group_name: s.group_name || "",
      isMaster: !String(s.id).startsWith("sup-") });
  };
  const saveSupplierEdit = async () => {
    if (!supEdit) return;
    if (!(supEdit.name || "").trim()) { alert("Party ka naam likhiye"); return; }
    if (!supEdit.group_id && !supEdit.group_name) { alert("Select 'Under (Ledger Group)' (e.g. Sundry Creditors)"); return; }
    setSupSaving(true);
    try {
      const body = { name: supEdit.name.trim(), gstin: supEdit.gstin || null, phone: supEdit.phone || "",
        address: supEdit.address || "", city: "", state: supEdit.state || "", state_code: supEdit.state_code || "",
        pincode: "", party_type: "purchase" };
      if (supEdit.group_id) { body.group_id = supEdit.group_id; body.group_name = supEdit.group_name; }
      if (supEdit.isMaster) {
        const all = await axios.get(`${API}/customers`);
        const c = (all.data || []).find((x) => x.id === supEdit.id) || {};
        await axios.put(`${API}/customers/${supEdit.id}`, { ...c, ...body, city: c.city || "", pincode: c.pincode || "" });
      } else {
        await axios.post(`${API}/customers`, { ...body, group_id: supEdit.group_id || "", group_name: supEdit.group_name || "" });
      }
      setSupEdit(null);
      const r = await axios.get(`${API}/suppliers`);
      setSuppliers(r.data.suppliers || []);
    } catch (e) { alert(e.response?.data?.detail || "Save failed"); }
    finally { setSupSaving(false); }
  };

  const filteredSuppliers = suppliers.filter(
    (s) =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.gstin || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-8" data-testid="customers-page">
      {view === null ? (
        <>
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-2">Customers</h1>
            <p className="text-slate-500 text-sm">Choose a section to view its parties</p>
          </div>
          <div className="max-w-2xl space-y-3">
            <button
              onClick={() => setView("sales")}
              data-testid="customers-open-sales"
              className="w-full flex items-center gap-4 px-6 py-6 border-2 border-blue-300 bg-blue-50/60 hover:bg-blue-100/70 text-blue-900 rounded-sm text-left transition-colors"
            >
              <TrendUp size={24} weight="duotone" />
              <span className="flex-1">
                <span className="block text-sm font-black uppercase tracking-wider">Sales</span>
                <span className="block text-xs text-slate-500 mt-0.5">{salesCustomers.length} customers (billed by you)</span>
              </span>
              <CaretRight size={18} weight="bold" />
            </button>
            <button
              onClick={() => setView("purchase")}
              data-testid="customers-open-purchase"
              className="w-full flex items-center gap-4 px-6 py-6 border-2 border-emerald-300 bg-emerald-50/60 hover:bg-emerald-100/70 text-emerald-900 rounded-sm text-left transition-colors"
            >
              <TrendDown size={24} weight="duotone" />
              <span className="flex-1">
                <span className="block text-sm font-black uppercase tracking-wider">Purchases</span>
                <span className="block text-xs text-slate-500 mt-0.5">{suppliers.length} suppliers / purchase clients (from your purchase bills)</span>
              </span>
              <CaretRight size={18} weight="bold" />
            </button>
          </div>
        </>
      ) : view === "purchase" ? (
        <>
          <div className="mb-8 flex items-center gap-3">
            <button onClick={() => setView(null)} data-testid="customers-back-btn"
              className="p-2 border border-slate-300 rounded-sm hover:bg-slate-100 text-slate-700">
              <CaretLeft size={18} weight="bold" />
            </button>
            <div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">Purchases — Parties</h1>
              <p className="text-slate-500 text-sm">Suppliers from your purchase bills and saved purchase clients</p>
            </div>
          </div>
          <div className="mb-6 relative">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" data-testid="search-suppliers-input" placeholder="Search by name or GSTIN..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" />
          </div>
          {filteredSuppliers.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-sm p-12 text-center" data-testid="suppliers-empty">
              <h3 className="text-xl font-bold text-slate-900 mb-2">No purchase parties yet</h3>
              <p className="text-slate-500">Suppliers appear here automatically once you record purchase bills.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm" data-testid="suppliers-list">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-900 text-white text-left">
                    <th className="px-4 py-3 text-xs font-black uppercase tracking-wider">Supplier Name</th>
                    <th className="px-4 py-3 text-xs font-black uppercase tracking-wider">GST Number</th>
                    <th className="px-4 py-3 text-xs font-black uppercase tracking-wider w-28">State Code</th>
                    <th className="px-4 py-3 text-xs font-black uppercase tracking-wider w-24 text-right">Bills</th>
                    <th className="px-4 py-3 text-xs font-black uppercase tracking-wider w-24 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.map((s) => (
                    <Fragment key={s.id}>
                      <tr data-testid={`supplier-row-${s.id}`}
                        className={`border-b border-slate-100 hover:bg-slate-50 ${expandedId === s.id ? "bg-emerald-50/50" : ""}`}>
                        <td className="px-4 py-3">
                          <button onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                            data-testid={`supplier-name-${s.id}`}
                            className="flex items-center gap-2 font-bold text-slate-900 hover:text-emerald-700 text-left">
                            {expandedId === s.id ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                            {s.name}
                          </button>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-600">{s.gstin || "—"}</td>
                        <td className="px-4 py-3 text-slate-700 font-semibold">{s.state_code || "—"}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{s.bills || 0}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={(e) => { e.stopPropagation(); openSupplierEdit(s); }}
                            data-testid={`edit-supplier-${s.id}`}
                            title="Edit party — Under (Ledger Group) set karein"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 hover:border-emerald-600 hover:text-emerald-700 text-slate-700 rounded-sm text-[11px] font-bold">
                            <Pencil size={12} weight="bold" /> Edit
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(s.id, s.name); }}
                            data-testid={`delete-supplier-${s.id}`}
                            title="Delete party everywhere"
                            className="ml-2 inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 hover:border-rose-600 hover:text-rose-700 text-slate-700 rounded-sm text-[11px] font-bold">
                            <Trash size={12} weight="bold" /> Delete
                          </button>
                        </td>
                      </tr>
                      {expandedId === s.id && (
                        <tr data-testid={`supplier-detail-${s.id}`}>
                          <td colSpan={5} className="px-6 py-4 bg-emerald-50/40 border-b border-emerald-100">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-sm">
                              <p className="text-slate-700"><span className="font-bold text-slate-500 text-xs uppercase tracking-wider block">Address</span>{s.address || "—"}</p>
                              <p className="text-slate-700"><span className="font-bold text-slate-500 text-xs uppercase tracking-wider block">State</span>{s.state || "—"} ({s.state_code || "—"})</p>
                              <p className="text-slate-700"><span className="font-bold text-slate-500 text-xs uppercase tracking-wider block">Phone</span>{s.phone || "—"}</p>
                              <p className="text-slate-700"><span className="font-bold text-slate-500 text-xs uppercase tracking-wider block">Under (Group)</span>{s.group_name || "—"}</p>
                              <p className="text-slate-700"><span className="font-bold text-slate-500 text-xs uppercase tracking-wider block">GSTIN</span><span className="font-mono text-xs">{s.gstin || "—"}</span></p>
                              <p className="text-slate-700"><span className="font-bold text-slate-500 text-xs uppercase tracking-wider block">Purchase Bills</span>{s.bills || 0}</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {supEdit && (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="supplier-edit-modal">
              <div className="bg-white rounded-sm shadow-2xl w-full max-w-md overflow-hidden">
                <div className="px-5 py-4 bg-emerald-900 text-white flex items-center justify-between">
                  <h3 className="text-base font-black">Edit Purchase Party</h3>
                  <button onClick={() => setSupEdit(null)} className="text-emerald-200 hover:text-white text-2xl leading-none" data-testid="supplier-edit-close">×</button>
                </div>
                <div className="p-5 space-y-3 max-h-[75vh] overflow-y-auto">
                  <div>
                    <Label className="text-xs">Party Name *</Label>
                    <Input value={supEdit.name} onChange={(e) => setSupEdit({ ...supEdit, name: e.target.value })} data-testid="supplier-edit-name" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">GSTIN</Label>
                      <Input value={supEdit.gstin} onChange={(e) => setSupEdit({ ...supEdit, gstin: e.target.value.toUpperCase() })} maxLength={15} data-testid="supplier-edit-gstin" />
                    </div>
                    <div>
                      <Label className="text-xs">Phone</Label>
                      <Input value={supEdit.phone} onChange={(e) => setSupEdit({ ...supEdit, phone: e.target.value })} data-testid="supplier-edit-phone" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Address</Label>
                    <Input value={supEdit.address} onChange={(e) => setSupEdit({ ...supEdit, address: e.target.value })} data-testid="supplier-edit-address" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">State</Label>
                      <Input value={supEdit.state} onChange={(e) => setSupEdit({ ...supEdit, state: e.target.value })} data-testid="supplier-edit-state" />
                    </div>
                    <div>
                      <Label className="text-xs">State Code</Label>
                      <Input value={supEdit.state_code} onChange={(e) => setSupEdit({ ...supEdit, state_code: e.target.value })} data-testid="supplier-edit-state-code" />
                    </div>
                  </div>
                  <div data-testid="supplier-edit-group-picker">
                    <Label className="text-xs">Under (Ledger Group) * — jaise Sundry Creditors / Sundry Debtors</Label>
                    <GroupPicker groups={groups} value={supEdit.group_id}
                      onChange={(g) => setSupEdit({ ...supEdit, group_id: g.id, group_name: g.name })}
                      onGroupCreated={(g) => setGroups((gs) => [...gs, g].sort((a, b) => a.name.localeCompare(b.name)))}
                      testid="supplier-edit-group" />
                    {supEdit.group_name && !supEdit.group_id && <p className="text-[11px] text-slate-500 mt-1">Current: {supEdit.group_name}</p>}
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setSupEdit(null)} data-testid="supplier-edit-cancel">Cancel</Button>
                    <Button size="sm" onClick={saveSupplierEdit} disabled={supSaving} data-testid="supplier-edit-save" className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold">{supSaving ? "Saving…" : "Save Party"}</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
      <>
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => setView(null)} data-testid="customers-back-btn"
            className="p-2 border border-slate-300 rounded-sm hover:bg-slate-100 text-slate-700">
            <CaretLeft size={18} weight="bold" />
          </button>
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-2">
              Sales — Customers
            </h1>
            <p className="text-slate-500 text-sm">Manage your customer database</p>
          </div>
        </div>
        <button
          onClick={() => setIsDialogOpen(true)}
          data-testid="add-customer-btn"
          className="px-4 py-2 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 transition-all duration-200 flex items-center gap-2"
        >
          <Plus size={20} weight="bold" />
          Add Customer
        </button>
      </div>

      {/* Search */}
      <div className="mb-6 relative">
        <MagnifyingGlass
          size={20}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          type="text"
          data-testid="search-customers-input"
          placeholder="Search by name, email or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        />
      </div>

      {/* Customers Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-48 bg-white border border-slate-200 rounded-sm animate-pulse"></div>
          ))}
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/c2875c4c-3209-4e71-8b15-de610df9cec1/images/6f94e3573c58a84c73fc78db7a1660d6d91c6b0e4489f8721400bcc17382d354.png"
            alt="No customers"
            className="w-64 h-64 mx-auto mb-6 opacity-60"
          />
          <h3 className="text-xl font-bold text-slate-900 mb-2">No customers found</h3>
          <p className="text-slate-500 mb-6">Add your first customer to get started</p>
          <button
            onClick={() => setIsDialogOpen(true)}
            data-testid="empty-add-customer-btn"
            className="px-6 py-3 bg-blue-600 text-white rounded-sm font-semibold hover:bg-blue-700 transition-all duration-200"
          >
            Add Customer
          </button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="customers-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-900 text-white text-left">
                <th className="px-4 py-3 text-xs font-black uppercase tracking-wider">Customer Name</th>
                <th className="px-4 py-3 text-xs font-black uppercase tracking-wider">GST Number</th>
                <th className="px-4 py-3 text-xs font-black uppercase tracking-wider w-28">State Code</th>
                <th className="px-4 py-3 text-xs font-black uppercase tracking-wider w-28 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <Fragment key={customer.id}>
                  <tr
                    data-testid={`customer-row-${customer.id}`}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${expandedId === customer.id ? "bg-blue-50/50" : ""}`}
                  >
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setExpandedId(expandedId === customer.id ? null : customer.id)}
                        data-testid={`customer-name-${customer.id}`}
                        className="flex items-center gap-2 font-bold text-slate-900 hover:text-blue-700 text-left"
                      >
                        {expandedId === customer.id ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                        {customer.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{customer.gstin || "—"}</td>
                    <td className="px-4 py-3 text-slate-700 font-semibold">{customer.state_code || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => handleEdit(customer)}
                          data-testid={`edit-customer-${customer.id}`}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-sm transition-colors"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(customer.id, customer.name)}
                          data-testid={`delete-customer-${customer.id}`}
                          className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === customer.id && (
                    <tr key={`${customer.id}-detail`} data-testid={`customer-detail-${customer.id}`}>
                      <td colSpan={4} className="px-6 py-4 bg-blue-50/40 border-b border-blue-100">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-sm">
                          <p className="text-slate-700"><span className="font-bold text-slate-500 text-xs uppercase tracking-wider block">Address</span>{customer.address || "—"}{customer.city ? `, ${customer.city}` : ""}{customer.pincode ? ` - ${customer.pincode}` : ""}</p>
                          <p className="text-slate-700"><span className="font-bold text-slate-500 text-xs uppercase tracking-wider block">Phone</span>{customer.phone || "—"}</p>
                          <p className="text-slate-700"><span className="font-bold text-slate-500 text-xs uppercase tracking-wider block">Email</span>{customer.email || "—"}</p>
                          <p className="text-slate-700"><span className="font-bold text-slate-500 text-xs uppercase tracking-wider block">State</span>{customer.state || "—"} ({customer.state_code || "—"})</p>
                          <p className="text-slate-700"><span className="font-bold text-slate-500 text-xs uppercase tracking-wider block">Under (Group)</span>{customer.group_name || "—"}</p>
                          <p className="text-slate-700"><span className="font-bold text-slate-500 text-xs uppercase tracking-wider block">GSTIN</span><span className="font-mono text-xs">{customer.gstin || "—"}</span></p>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          aria-describedby={undefined}
          onInteractOutside={(e) => {
            const t = e.detail?.originalEvent?.target || e.target;
            if (t?.closest?.('[data-group-picker-panel]')) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingCustomer ? "Edit Customer" : "Add New Customer"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">{custFcfg.label("name", "Name")} *</Label>
                <Input
                  id="name"
                  autoCap
                  data-testid="customer-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={!custFcfg.canEdit("name")}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  data-testid="customer-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="group">Under (Ledger Group) *</Label>
              <GroupPicker
                groups={groups}
                value={formData.group_id}
                onChange={(g) => setFormData({ ...formData, group_id: g.id })}
                onGroupCreated={(g) => setGroups((gs) => [...gs, g].sort((a, b) => a.name.localeCompare(b.name)))}
                testid="customer-group-picker"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="customer-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="gstin">GSTIN</Label>
                <Input
                  id="gstin"
                  data-testid="customer-gstin-input"
                  value={formData.gstin}
                  onChange={(e) => handleGstinChange(e.target.value)}
                  placeholder="Type 15-digit GSTIN to auto-fetch"
                  maxLength={15}
                />
                {gstinFetch.state !== "idle" && (
                  <p data-testid="gstin-fetch-status" className={`mt-1 text-xs font-semibold ${
                    gstinFetch.state === "done" ? "text-emerald-700" :
                    gstinFetch.state === "loading" ? "text-blue-600" :
                    gstinFetch.state === "partial" ? "text-amber-700" : "text-rose-600"
                  }`}>
                    {gstinFetch.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                autoCap
                data-testid="customer-address-input"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  data-testid="customer-city-input"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="state">State *</Label>
                <select
                  id="state"
                  data-testid="customer-state-select"
                  value={formData.state}
                  onChange={(e) => handleStateChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                >
                  {INDIAN_STATES.map((state) => (
                    <option key={state.code} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="pincode">Pincode *</Label>
                <Input
                  id="pincode"
                  data-testid="customer-pincode-input"
                  value={formData.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  required
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-customer-btn">
                {editingCustomer ? "Update" : "Add"} Customer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}